import React, { useState } from "react";
import { Model, BATTLE_SCARS } from "./types";
import { useForceSaveOnUnmount } from "./hooks/useAutoSave";

interface AdvanceDayModalProps {
  models: Model[];
  onConfirm: (deadModelIds: string[], choice: "reinforce" | "explore", injuries?: {[modelId: string]: string}) => void;
  onClose: () => void;
}

export const AdvanceDayModal = ({ models, onConfirm, onClose }: AdvanceDayModalProps) => {
  // Force save when modal closes
  useForceSaveOnUnmount();

  const [step, setStep] = useState(1); // 1 = death selection, 2 = injury selection, 3 = explore/reinforce choice
  const [selectedDead, setSelectedDead] = useState<string[]>([]);
  const [injuries, setInjuries] = useState<{[modelId: string]: string}>({});

  const handleDeathConfirm = () => {
    if (selectedDead.length === 0) {
      // No deaths, proceed to choice
      setStep(3);
    } else {
      // Check if any selected models are elites
      const hasElites = selectedDead.some(modelId => {
        const model = models.find(m => m.id === modelId);
        return model && (model.type || "trooper") === 'elite';
      });

      if (hasElites) {
        // Need to select injuries for elites
        setStep(2);
      } else {
        // Only troopers, proceed to choice
        setStep(3);
      }
    }
  };

  const handleChoice = (choice: "reinforce" | "explore") => {
    onConfirm(selectedDead, choice, injuries);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-40 p-3">
      <div className="ledger-shell p-4 max-w-2xl w-full max-h-[90vh] overflow-y-auto text-sm">
        {step === 1 && (
          <>
            <h2 className="text-base font-semibold uppercase tracking-wider mb-3">
              Advance to Next Battle
            </h2>
            <p className="text-[#E6E5D3] mb-3">
              Select models who died this battle:
            </p>
            <div className="space-y-2 mb-6">
              {models
                .filter((m) => m.alive)
                .map((model) => (
                  <label
                    key={model.id}
                    className="flex items-center gap-3 bg-gray-700 px-4 py-3 rounded cursor-pointer hover:bg-gray-600"
                  >
                    <input
                      type="checkbox"
                      checked={selectedDead.includes(model.id)}
                      onChange={() =>
                        setSelectedDead(
                          selectedDead.includes(model.id)
                            ? selectedDead.filter((id) => id !== model.id)
                            : [...selectedDead, model.id]
                        )
                      }
                      className="w-5 h-5"
                    />
                    <span className="text-white">
                      {model.name} ({model.class}) -{" "}
                      {(model.type || "trooper").toUpperCase()}
                    </span>
                  </label>
                ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleDeathConfirm}
                className="flex-1 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
              >
                Continue
              </button>
              <button
                onClick={onClose}
                className="flex-1 bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <h2 className="text-2xl font-bold text-white mb-4">
              Elite Injuries
            </h2>
            <p className="text-gray-300 mb-4">
              Select injuries for elite models who died:
            </p>
            <div className="space-y-4 mb-6">
              {selectedDead
                .map(modelId => models.find(m => m.id === modelId))
                .filter(model => model && (model.type || "trooper") === 'elite')
                .map(model => model && (
                  <div key={model.id} className="bg-gray-700 rounded p-4">
                    <h3 className="text-lg font-bold text-yellow-500 mb-3">
                      {model.name} - Select Injury
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      {BATTLE_SCARS.map((scar) => (
                        <label
                          key={scar}
                          className="flex items-center gap-2 bg-gray-600 px-3 py-2 rounded cursor-pointer hover:bg-gray-500"
                        >
                          <input
                            type="radio"
                            name={`injury-${model.id}`}
                            value={scar}
                            checked={injuries[model.id] === scar}
                            onChange={() => setInjuries({...injuries, [model.id]: scar})}
                            className="w-4 h-4"
                          />
                          <span className="text-gray-300 text-sm">{scar}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setStep(3)}
                disabled={Object.keys(injuries).length !== selectedDead.filter(modelId => {
                  const model = models.find(m => m.id === modelId);
                  return model && (model.type || "trooper") === 'elite';
                }).length}
                className="flex-1 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue
              </button>
              <button
                onClick={() => setStep(1)}
                className="flex-1 bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
              >
                Back
              </button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <h2 className="text-2xl font-bold text-white mb-4">
              Choose Your Action
            </h2>
            <p className="text-gray-300 mb-6">
              Will you explore the battlefield or reinforce your warband?
            </p>
            <div className="space-y-3">
              <button
                onClick={() => handleChoice("explore")}
                className="w-full bg-blue-600 text-white px-6 py-4 rounded hover:bg-blue-700 text-lg font-semibold"
              >
                🔍 Explore
              </button>
              <button
                onClick={() => handleChoice("reinforce")}
                className="w-full bg-green-600 text-white px-6 py-4 rounded hover:bg-green-700 text-lg font-semibold"
              >
                💰 Reinforce
              </button>
            </div>
            <button
              onClick={onClose}
              className="w-full mt-4 bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
            >
              Cancel
            </button>
          </>
        )}
      </div>
    </div>
  );
};
